import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpCircle, X, ChevronRight, Sparkles, Play, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface TutorialStep {
  title: string;
  description: string;
  icon: React.ElementType;
  action?: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    title: "Choose Your Wellness Focus",
    description: "Browse categories like Meditation, Sleep, or Anxiety Relief to find what speaks to you right now.",
    icon: Sparkles,
    action: "Browse Categories"
  },
  {
    title: "Start Simple",
    description: "Try a sample session first - no setup required! Just click and listen to experience the quality.",
    icon: Play,
    action: "Try Sample Session"
  },
  {
    title: "Personalize Your Experience",
    description: "Create custom sessions by filling out a quick questionnaire about your preferences and goals.",
    icon: Settings,
    action: "Personalize Sessions"
  }
];

interface UserGuidanceProps {
  variant?: 'welcome' | 'help' | 'floating';
  onClose?: () => void;
}

export const UserGuidance: React.FC<UserGuidanceProps> = ({ 
  variant = 'welcome', 
  onClose 
}) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isExpanded, setIsExpanded] = useState(variant === 'welcome');

  if (variant === 'floating') {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        {!isExpanded ? (
          <Button
            onClick={() => setIsExpanded(true)}
            className="rounded-full w-14 h-14 bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <HelpCircle className="h-6 w-6" />
          </Button>
        ) : (
          <Card className="w-80 shadow-xl border-primary/20">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg">Quick Help</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Choose a Category</p>
                    <p className="text-xs text-muted-foreground">Start with what you need most right now</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Play className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Try Sample Sessions</p>
                    <p className="text-xs text-muted-foreground">No setup required - instant wellness</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Personalize Later</p>
                    <p className="text-xs text-muted-foreground">Add your preferences for custom sessions</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setIsExpanded(false)}
                className="w-full"
                size="sm"
              >
                Got it!
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  if (variant === 'welcome') {
    return (
      <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl">Welcome to Your Wellness Journey!</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {user ? `Hello ${user.email?.split('@')[0]}!` : ''} Here's how to get started in 3 simple steps:
            </p>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {tutorialSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-background/50">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon className="h-4 w-4 text-primary" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm mb-1">{step.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default UserGuidance;